import { useMemo } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface PickerOption {
  id: string;
  label: string;
  secondary?: string;
}

export interface SearchPickerProps {
  value: string;
  placeholder: string;
  open: boolean;
  search: string;
  onOpenChange: (open: boolean) => void;
  onSearchChange: (next: string) => void;
  loading?: boolean;
  options: PickerOption[];
  onSelect: (id: string) => void;
  clearable?: boolean;
  onClear?: () => void;
}

/**
 * Generic search-picker shown beneath an input field. Used by the Payer
 * (Insurance / Corporate / Patient Package), TPA, and Service List pickers
 * inside billing dialogs. Mirrors Flutter `FieldType.SearchPicker`.
 *
 * Options are filtered client-side against the trimmed search query
 * (case-insensitive, against `label` and `secondary`). The caller is
 * responsible for fetching the option set — typically a React-Query hook
 * with debounced server-side search and a `keepPreviousData` placeholder.
 */
export function SearchPicker({
  value,
  placeholder,
  open,
  search,
  onOpenChange,
  onSearchChange,
  loading,
  options,
  onSelect,
  clearable,
  onClear,
}: SearchPickerProps) {
  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.trim().toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.secondary ?? '').toLowerCase().includes(q),
    );
  }, [options, search]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          onOpenChange(!open);
          onSearchChange('');
        }}
        className="w-full h-10 px-3 flex items-center justify-between bg-grey-100 rounded-md text-sm text-left"
      >
        <span
          className={cn(
            'truncate',
            value ? 'text-text-primary' : 'text-text-hint',
          )}
        >
          {value || placeholder}
        </span>
        <ChevronDown
          className="w-4 h-4 text-text-muted shrink-0 ml-2"
          aria-hidden
        />
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-label="Close picker"
            onClick={() => onOpenChange(false)}
            className="fixed inset-0 z-10 cursor-default"
          />
          <div className="absolute z-20 left-0 right-0 top-full mt-1 rounded-md border border-border-default bg-surface-card shadow-lg overflow-hidden">
            <div className="p-2 border-b border-border-subtle">
              <div className="relative">
                <Search
                  className="absolute left-3 top-3 h-4 w-4 text-text-hint pointer-events-none"
                  aria-hidden
                />
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder="Search…"
                  className="w-full h-10 pl-8 pr-3 rounded-md bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:outline-none focus:ring-1 focus:ring-brand-primary"
                />
              </div>
            </div>
            <div className="max-h-[280px] overflow-y-auto scrollbar-hide py-1">
              {clearable && (
                <button
                  type="button"
                  onClick={() => {
                    onClear?.();
                    onOpenChange(false);
                  }}
                  className="w-full px-3 py-1.5 text-left text-sm text-status-error hover:bg-grey-100"
                >
                  Clear selection
                </button>
              )}
              {loading && filtered.length === 0 ? (
                <p className="text-sm text-text-muted text-center py-4">
                  Loading…
                </p>
              ) : filtered.length === 0 ? (
                <p className="text-sm text-text-muted text-center py-4">
                  No matches
                </p>
              ) : (
                filtered.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => {
                      onSelect(o.id);
                      onOpenChange(false);
                    }}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm text-text-primary hover:bg-grey-100"
                  >
                    <span className="truncate">{o.label}</span>
                    {o.secondary && (
                      <span className="text-xs text-text-muted shrink-0">
                        {o.secondary}
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
